import { Button, Col, DatePicker, Input, Row, Select } from 'antd';
import dayjs from 'dayjs';
import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AddSvg, SearchSvg } from 'utils/svgs';
import { checkPermission } from 'utils/utils';
import styles from './index.module.scss';
import { SearchAndFilterProps } from './types';

const { RangePicker } = DatePicker;

const selectNarrow = { width: 108 };
const selectMedium = { width: 118 };

const SearchAndFilter: FC<SearchAndFilterProps> = (props) => {
  const {
    performSearchHandler,
    searchValue,
    openDrawerHandler,
    title,
    setSearch,
    totalRecords,
    statusFilter,
    onStatusFilterChange,
    categoryFilter,
    onCategoryFilterChange,
    subCategoryFilter,
    onSubCategoryFilterChange,
    categories = [],
    selectedDate,
    setSelectedDate,
  } = props;

  const userPermissions = useSelector((state: any) => state?.userProfile?.data);
  const isComboQcViewAll = checkPermission(userPermissions?.permissions, {
    permissionName: 'ComboQualityCheck',
    permission: ['viewAll'],
  });
  const showStatusCategoryFilters =
    userPermissions?.isAdmin === true || isComboQcViewAll;

  const isAddPermission = checkPermission(userPermissions?.permissions, {
    permissionName: 'ComboItems',
    permission: ['add'],
  });

  const subCategoryOptions = useMemo(() => {
    if (!categoryFilter) return [];
    const category = categories.find((c) => c.id === categoryFilter);
    return (category?.subCategories || []).map((sub) => ({
      label: sub.name,
      value: sub.id,
    }));
  }, [categoryFilter, categories]);

  return (
    <div className={styles['search-filter']}>
      <div className={styles['search-filter__wrapper']}>
        <Row className={styles['search-filter__header']} align="middle" wrap={false}>
          <Col flex="none">
            <div className={styles['search-filter__title']}>
              {title}
              <span className={styles['search-filter__title--count']}>
                ({totalRecords})
              </span>
            </div>
          </Col>
          <Col flex="auto" style={{ minWidth: 0 }}>
            <div className={styles['search-filter__toolbar']}>
              <Button
                size="middle"
                className={styles['search-filter__clearBtn']}
                onClick={() => {
                  setSearch('');
                  if (showStatusCategoryFilters) {
                    onStatusFilterChange?.(null);
                    onCategoryFilterChange?.(null);
                    onSubCategoryFilterChange?.(null);
                  }
                  setSelectedDate?.(null);
                }}
              >
                Clear all
              </Button>

              <Input
                className={styles['search-filter__search']}
                placeholder="Search combo..."
                prefix={<SearchSvg />}
                onChange={performSearchHandler}
                value={searchValue}
                size="middle"
                allowClear
              />

              {showStatusCategoryFilters && (
                <>
                  <Select
                    style={selectNarrow}
                    value={statusFilter ?? undefined}
                    placeholder="Status"
                    size="middle"
                    onChange={(value) => onStatusFilterChange?.(value ?? null)}
                    allowClear
                    options={[
                      { label: 'Pending', value: 'PENDING' },
                      { label: 'Rejected', value: 'REJECTED' },
                      { label: 'Approved', value: 'APPROVED' },
                    ]}
                  />
                  <Select
                    style={selectMedium}
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label ?? '')
                        .toString()
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    value={categoryFilter ?? undefined}
                    placeholder="Category"
                    size="middle"
                    onChange={(value) => {
                      onCategoryFilterChange?.(value ?? null);
                      onSubCategoryFilterChange?.(null);
                    }}
                    allowClear
                    options={categories.map((c) => ({
                      label: c.name,
                      value: c.id,
                    }))}
                  />
                  <Select
                    style={selectMedium}
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label ?? '')
                        .toString()
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    value={subCategoryFilter ?? undefined}
                    placeholder="Sub Category"
                    size="middle"
                    disabled={!categoryFilter}
                    onChange={(value) => onSubCategoryFilterChange?.(value ?? null)}
                    allowClear
                    options={subCategoryOptions}
                  />
                </>
              )}

              <RangePicker
                className={styles['search-filter__dateRange']}
                picker="date"
                format="DD/MM/YYYY"
                size="middle"
                allowClear
                onChange={(value) => {
                  if (!value || !value[0] || !value[1]) {
                    setSelectedDate?.(null);
                  } else {
                    setSelectedDate?.([
                      dayjs(value[0]).startOf('day'),
                      dayjs(value[1]).endOf('day'),
                    ]);
                  }
                }}
                value={selectedDate}
              />

              {isAddPermission && openDrawerHandler && (
                <div
                  className={`${styles['user-table__action']} ${styles['search-filter__addBtn']}`}
                >
                  <button
                    type="button"
                    className={`btn-blue ${styles['user-table__action--button']}`}
                    onClick={openDrawerHandler}
                  >
                    <AddSvg />
                    <p>Add Combo</p>
                  </button>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default SearchAndFilter;
